import { process, State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridPagerSettings,
  GridRowProps,
} from '@progress/kendo-react-grid';
import { cloneElement, useCallback, useMemo, useState } from 'react';
import {
  subVarianceLineItems,
  VarianceLineItemColumns,
  VariancePeriodColumns,
} from 'src/assets/static/variance_details/VarianceLineItemColumns';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import Accordion from 'src/components/design-system/Accordion';
import GridWrapper from 'src/components/GridWrapper';
import { handleScrollDown } from 'src/hooks/useScrollDown';
import useVarianceDetails from 'src/hooks/useVarianceDetails';
import { IInvoiceLineItem } from 'src/types/InvoiceLineItem';
import { IPeriodSummaries } from 'src/types/VarianceDetails';
import VarianceDetailsToolBar, { DropDownOption, varianceOptions } from './VarianceDetailsToolBar';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const initialDataState: State = {
  skip: 0,
  take: 20,
};

const defaultProps = {
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  pageable: gridPagerSettings,
  showColumnMenu: true,
  tableTextId: 'Line Items',
};

const VarianceLineItems = () => {
  const { varianceId, variances, isVarianceLoading, varianceLineItems, setVarianceLineItems } =
    useVarianceDetails();

  const [dataState, setDataState] = useState(initialDataState);
  const { data, total } = useMemo(
    () => process(varianceLineItems || [], dataState),
    [dataState, varianceLineItems]
  );
  const [varianceDropDownValue, setVarianceDropDownValue] = useState<DropDownOption>(
    varianceOptions[0]
  );

  const clearFilters = useCallback(() => {
    setDataState(initialDataState);
    setVarianceDropDownValue(varianceOptions[0]);
  }, []);

  const handleDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => setDataState(e.dataState),
    []
  );

  const handleExpand = useCallback(
    (e: GridExpandChangeEvent) => {
      setVarianceLineItems((prev) =>
        [...prev].map((item) => {
          if (item?.rowId === e.dataItem.rowId) {
            item.expanded = !e.dataItem.expanded;
          }
          return item;
        })
      );
    },
    [setVarianceLineItems]
  );

  const showVariance = useCallback(() => {
    const varianceLineItems = variances.lineItemVariances.filter(
      (lineItem) =>
        lineItem.hasGlCodeVariance ||
        lineItem.hasUOMVariance ||
        lineItem.hasQtyVariance ||
        lineItem.hasSubTotalVariance
    );
    setVarianceLineItems(varianceLineItems);
  }, [setVarianceLineItems, variances.lineItemVariances]);

  const onVarianceDropDownChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { key } = e.value;
      if (key === 1) {
        setVarianceLineItems([...variances?.lineItemVariances]);
      } else {
        showVariance();
      }
      setVarianceDropDownValue(e.value);
    },
    [setVarianceLineItems, showVariance, variances?.lineItemVariances]
  );

  const SecondDetailComponent = (props: GridDetailRowProps) => {
    const secondChildItems = props.dataItem.invoices as IInvoiceLineItem[];
    return (
      <GridWrapper
        dataSource={secondChildItems}
        columns={subVarianceLineItems}
        DataItemKey="rowId"
        resizable
        isCustomExpandableRow={true}
        className="no_Veritcal_ScrollBar hide-scroll-bar"
      />
    );
  };

  const DetailComponent = (props: GridDetailRowProps) => {
    const firstChildItems = props.dataItem.periodSummaries as IPeriodSummaries[];
    return (
      <GridWrapper
        dataSource={firstChildItems}
        DataItemKey="rowId"
        columns={VariancePeriodColumns}
        detail={SecondDetailComponent}
        resizable
        isCustomExpandableRow={true}
        className="no_Veritcal_ScrollBar hide-scroll-bar"
      />
    );
  };

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    const style = { backgroundColor: '#ddd' };
    let trProps: any = {};
    if (props?.isAltRow) {
      trProps.style = style;
    }
    return cloneElement(trElement, { ...trProps });
  };

  return (
    <Accordion title={`Line items for Variance ${varianceId}`} defaultExpanded>
      <GridWrapper
        id="VarianceLineItemGrid"
        rowRender={rowRender}
        columns={VarianceLineItemColumns}
        dataSource={data}
        dataState={dataState}
        total={total}
        gridToolbarChildren={
          <VarianceDetailsToolBar
            clearFilter={clearFilters}
            showFilter
            state={dataState}
            onVarianceDropDownChange={onVarianceDropDownChange}
            varianceDropDownValue={varianceDropDownValue}
          />
        }
        onDataStateChange={handleDataStateChange}
        detail={DetailComponent}
        onExpandChange={handleExpand}
        loading={isVarianceLoading}
        isCustomExpandableRow={true}
        className={'max-h-[400px] mb-5'}
        {...defaultProps}
        onColumnResize={handleScrollDown}
      />
    </Accordion>
  );
};

export default VarianceLineItems;
